<script>
import Swal from 'sweetalert2';
import VTextbox from '../components/Textbox.vue';
import VButton from '../components/Button.vue';
import { isValidCNPJ, normalizeCNPJ } from '../helpers/cnpj';
import {
  clearCardsCache,
  listCardsAndProducts
} from '../helpers/cards-list';

export default {
  name: 'P2PTransfer',
  components: {
    VTextbox,
    VButton
  },
  data() {
    return {
      sourceCardId: '',
      destinationCardId: '',
      destinationCardNumber: '',
      value: '',
      description: '',
      sourceCardBalance: 0,
      cards: [],
      showForm: false,
      minValue: 1,
      maxValue: 50000,
      user: {}
    };
  },
  computed: {
    // Cartões disponíveis para seleção de origem
    availableSourceCards() {
      return this.cards.filter(card => {
        // Filtra cartões ativos e que pertencem ao mesmo CNPJ
        return (
          card.idStatusCartao === 1
          && this.isPjCard(card)
          && card.idConta !== Number(this.destinationCardId)
        );
      });
    },
    // Cartão de origem selecionado
    selectedSourceCard() {
      return this.cards.find(
        card => card.idConta === Number(this.sourceCardId)
      );
    }
  },
  watch: {
    destinationCardNumber(value) {
      const sanitized = (value || '').replace(/\D/g, '');
      if (!sanitized) {
        this.destinationCardId = '';
        return;
      }

      const destinationCard = this.cards.find(card => {
        const cardNumber = (card.numeroCartao || '').replace(/\D/g, '');
        return cardNumber === sanitized;
      });

      if (destinationCard) {
        this.destinationCardId = destinationCard.idConta;
      } else {
        this.destinationCardId = '';
      }
    }
  },
  methods: {
    attachBalancesToCards(cards = []) {
      return (cards || []).map(card => {
        const amount = card.saldo != null ? Number(card.saldo) : Number(card.balance);
        return {
          ...card,
          saldo: Number.isNaN(amount) ? 0 : amount
        };
      });
    },
    // Verifica se é cartão PJ
    isPjCard(card) {
      return (
        card.idProduto === 4 || card.idProduto === 5 || card.idProduto === 6
      );
    },
    // Carrega saldo do cartão de origem
    loadSourceCardBalance() {
      if (!this.sourceCardId) return;

      const selectedCard = this.selectedSourceCard;

      if (selectedCard && typeof selectedCard.saldo === 'number') {
        this.sourceCardBalance = selectedCard.saldo;
      } else if (selectedCard && typeof selectedCard.balance === 'number') {
        this.sourceCardBalance = selectedCard.balance;
      } else {
        this.sourceCardBalance = 0;
      }
    },
    // Quando seleciona o cartão de origem
    async onSourceCardChange() {
      this.showForm = true;
      this.destinationCardNumber = '';
      this.destinationCardId = '';
      await this.loadSourceCardBalance();
    },
    // Valida o formulário
    validateForm() {
      if (!this.sourceCardId) {
        this.$store.commit('message', {
          type: 'error',
          text: 'Selecione o cartão de origem.'
        });
        return false;
      }

      const sanitizedDestination = (this.destinationCardNumber || '').replace(
        /\D/g,
        ''
      );

      if (!sanitizedDestination) {
        this.$store.commit('message', {
          type: 'error',
          text: 'Informe o cartão de destino.'
        });
        return false;
      }

      if (sanitizedDestination.length !== 16) {
        this.$store.commit('message', {
          type: 'error',
          text: 'Número do cartão de destino inválido.'
        });
        return false;
      }

      const numValue = Number(this.value);

      if (!this.value || numValue <= 0) {
        this.$store.commit('message', {
          type: 'error',
          text: 'Informe o valor da transferência.'
        });
        return false;
      }

      if (numValue < this.minValue) {
        this.$store.commit('message', {
          type: 'error',
          text: `O valor mínimo para transferência é ${this.minValue.toLocaleString(
            'pt-BR',
            { style: 'currency', currency: 'BRL' }
          )}.`
        });
        return false;
      }

      if (numValue > this.maxValue) {
        this.$store.commit('message', {
          type: 'error',
          text: `O valor máximo para transferência é ${this.maxValue.toLocaleString(
            'pt-BR',
            { style: 'currency', currency: 'BRL' }
          )}.`
        });
        return false;
      }

      if (numValue > this.sourceCardBalance) {
        this.$store.commit('message', {
          type: 'error',
          text: 'Saldo insuficiente no cartão de origem.'
        });
        return false;
      }

      if (!this.description || this.description.trim().length === 0) {
        this.$store.commit('message', {
          type: 'error',
          text: 'Informe uma descrição para a transferência.'
        });
        return false;
      }

      return true;
    },
    // Submete o formulário
    async sendForm() {
      if (this.$store.state.formLoading) return false;

      if (!this.validateForm()) return false;

      try {
        this.$store.commit('formLoading', true);

        const sanitizedDestination = (this.destinationCardNumber || '').replace(
          /\D/g,
          ''
        );
        const sourceCardNumber = this.selectedSourceCard && this.selectedSourceCard.numeroCartao
          ? this.selectedSourceCard.numeroCartao
          : '';
        const sanitizedSource = sourceCardNumber.replace(/\D/g, '');

        if (!sanitizedDestination) {
          this.$store.commit('message', {
            type: 'error',
            text: 'Informe o cartão de destino.'
          });
          this.$store.commit('formLoading', false);
          return false;
        }

        if (sanitizedDestination.length !== 16) {
          this.$store.commit('message', {
            type: 'error',
            text: 'Número do cartão de destino inválido.'
          });
          this.$store.commit('formLoading', false);
          return false;
        }

        if (
          sanitizedDestination
          && sanitizedSource
          && sanitizedDestination === sanitizedSource
        ) {
          this.$store.commit('message', {
            type: 'error',
            text: 'Os cartões de origem e destino devem ser diferentes.'
          });
          this.$store.commit('formLoading', false);
          return false;
        }

        const valueFormatted = (Number(this.value) || 0).toFixed(2);

        const document = this.user && this.user.cnpj ? normalizeCNPJ(this.user.cnpj) : '';

        const postData = {
          document,
          originalAccount: Number(this.sourceCardId),
          cardNumber: sanitizedDestination,
          value: valueFormatted,
          description: this.description.trim()
        };

        await this.$http.post('/transfers/p2p-transfers', postData);

        clearCardsCache();
        await this.loadCards();
        if (this.sourceCardId) {
          this.loadSourceCardBalance();
        }

        Swal.fire({
          html: `
            <div>
              <span class="material-symbols-outlined" style="font-size:3.5rem; color:#002323;">check_circle</span>
              <p style="line-height:21px; margin-top: 15px; font-size: 18px;">
                <strong>Transferência entre Cartões realizada com sucesso!</strong>
                </br>
                <strong>O valor foi transferido</strong>
                </br>
                <strong>entre os cartões selecionados.</strong>
              </p>
            </div>
          `,
          confirmButtonText: 'Ok',
          confirmButtonColor: '#002323'
        }).then(() => {
          // Limpa o formulário
          this.sourceCardId = '';
          this.destinationCardId = '';
          this.destinationCardNumber = '';
          this.value = '';
          this.description = '';
          this.sourceCardBalance = 0;
          this.showForm = false;
        });
      } catch (error) {
        let message = 'Houve um erro ao realizar a transferência, tente novamente mais tarde.';

        if (typeof error === 'string') {
          message = error;
        } else if (error && error.message) {
          ({ message } = error);
        }

        this.$store.commit('message', {
          type: 'error',
          text: message
        });
      } finally {
        this.$store.commit('formLoading', false);
      }
    },
    // Carrega os cartões do usuário
    async loadCards() {
      try {
        this.$store.commit('pageLoading', true);

        const cards = await this.loadCardsFromAPI();
        this.cards = this.attachBalancesToCards(cards);
      } catch (error) {
        this.$store.commit('message', {
          type: 'error',
          text: 'Erro ao carregar cartões.'
        });
      } finally {
        this.$store.commit('pageLoading', false);
      }
    },
    // Carrega cartões da API
    async loadCardsFromAPI() {
      const data = await listCardsAndProducts(this.$http);
      return (data && data.cards) || [];
    },
    // Verifica se operação é permitida
    async permitedOperation() {
      try {
        await this.$http.get('/utils/check-restricao');
      } catch (error) {
        Swal.fire({
          type: 'error',
          title: error.message || 'Transação não permitida nesse horário',
          confirmButtonText: 'Ok',
          customClass: {
            title: 'px-5'
          }
        }).then(() => {
          this.$router.push('/');
        });
      }
    },
    // Formata o nome do cartão para exibição
    getCardDisplayName(card) {
      const lastFourDigits = card.numeroCartao
        ? card.numeroCartao.slice(-4)
        : '****';
      return `Cartão •••• ${lastFourDigits} - ${(
        card.saldo || 0
      ).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
    }
  },
  async mounted() {
    // Carrega dados do usuário
    const userData = localStorage.getItem('user');
    if (userData) {
      this.user = JSON.parse(userData);

      // Valida se é usuário PJ
      if (!this.user.cnpj || this.user.cpf) {
        Swal.fire({
          type: 'error',
          title: 'Funcionalidade disponível apenas para contas PJ',
          text:
            'Esta funcionalidade está disponível apenas para contas de Pessoa Jurídica.',
          confirmButtonText: 'Ok'
        }).then(() => {
          this.$router.push('/');
        });
        return;
      }

      // Valida CNPJ
      if (!isValidCNPJ(this.user.cnpj)) {
        this.$store.commit('message', {
          type: 'error',
          text: 'CNPJ inválido.'
        });
      }
    }

    await this.permitedOperation();
    await this.loadCards();
  }
};
</script>

<template>
  <div class="p2p-transfer">
    <div class="p2p-transfer__content">
      <div class="p2p-transfer__info">
        <h2 class="p2p-transfer__info__title">
          Transferência entre Cartões - Contas PJ
        </h2>
        <p class="p2p-transfer__info__description">
          Realize transferências entre cartões vinculados ao mesmo CNPJ ou ao
          mesmo Grupo Econômico de forma rápida e segura.
        </p>
        <div class="p2p-transfer__info__rules">
          <p><strong>Regras e limites:</strong></p>
          <ul>
            <li>
              Valor mínimo:
              {{
                minValue.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL"
                })
              }}
            </li>
            <li>
              Valor máximo:
              {{
                maxValue.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL"
                })
              }}
            </li>
            <li>Disponível apenas para cartões PJ ativos</li>
            <li>
              Os cartões devem estar vinculados ao mesmo CNPJ ou ao mesmo Grupo
              Econômico
            </li>
            <li>Transferência processada em tempo real</li>
          </ul>
        </div>
      </div>

      <form @submit.prevent="sendForm" class="p2p-transfer__form">
        <!-- Seleção de Cartão de Origem -->
        <div class="textbox">
          <label class="textbox__label">Cartão de origem:</label>
          <select
            v-model="sourceCardId"
            @change="onSourceCardChange"
            class="textbox__input"
            required
          >
            <option value="">Selecione o cartão de origem</option>
            <option
              v-for="card in availableSourceCards"
              :key="card.idConta"
              :value="card.idConta"
            >
              {{ getCardDisplayName(card) }}
            </option>
          </select>
          <p
            v-if="sourceCardId && selectedSourceCard"
            class="p2p-transfer__form__balance"
          >
            Saldo disponível:
            {{
              sourceCardBalance.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL"
              })
            }}
          </p>
        </div>

        <!-- Formulário completo aparece após selecionar origem -->
        <div v-if="showForm">
          <!-- Seleção de Cartão de Destino -->
          <v-textbox
            v-model="destinationCardNumber"
            label="Cartão de destino:"
            name="destinationCardNumber"
            placeholder="0000 0000 0000 0000"
            mask="#### #### #### ####"
            required
          />

          <!-- Valor da Transferência -->
          <v-textbox
            v-model="value"
            label="Valor da transferência:"
            data-vv-as="Valor"
            required
            money
            name="value"
            validate="required"
          />

          <!-- Descrição -->
          <v-textbox
            v-model="description"
            data-vv-as="description"
            data-vv-scope="description"
            label="Descrição:"
            name="description"
            :maxlength="50"
            type="text"
            placeholder="Ex: Transferência para gastos operacionais"
            required
          />

          <!-- Checkbox de Confirmação -->
          <label
            class="p2p-transfer__form__terms"
            style="display: flex; align-items: center;"
          >
            <input
              type="checkbox"
              title="Confirmo que todos os dados da transferência estão corretos"
              required
            />
            Confirmo que todos os dados da transferência estão corretos
          </label>

          <!-- Botão de Envio -->
          <v-button
            label="Realizar Transferência"
            class="p2p-transfer__form__submit"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.p2p-transfer {
  background-color: var(--color-white);
  position: relative;
  min-height: 100vh;
  padding-bottom: 5rem;

  &__header {
    margin-bottom: 2rem;
  }

  &__content {
    @media (min-width: 992px) {
      max-width: 800px;
      margin: 0 auto;
    }
  }

  &__info {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background-color: var(--color-grey-lighten);
    border-radius: 0.5rem;
    border-left: 4px solid var(--color-primary);

    &__title {
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--color-primary);
      margin-bottom: 1rem;
    }

    &__description {
      font-size: 1rem;
      color: var(--color-grey-medium);
      margin-bottom: 1rem;
    }

    &__rules {
      font-size: 0.9rem;
      color: var(--color-grey-dark);

      p {
        margin-bottom: 0.5rem;
        font-weight: 600;
      }

      ul {
        margin: 0.5rem 0 0 1.5rem;
        padding: 0;

        li {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
      }
    }
  }

  &__form {
    background-color: var(--color-white);
    padding: 1.5rem;
    border-radius: 0.5rem;
    border: 1px solid var(--color-grey-light);

    &__balance {
      margin-top: 0.5rem;
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--color-primary);
    }

    &__terms {
      display: block;
      padding: 1rem 0;
      font-size: 0.9rem;

      input {
        display: inline-block;
        vertical-align: middle;
        margin-right: 0.5rem;
      }
    }

    &__submit {
      margin-top: 1rem;
      width: 100%;

      @media (min-width: 576px) {
        width: auto;
      }
    }
  }

  .textbox {
    margin-bottom: 1.5rem;

    &__label {
      display: block;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: var(--color-grey-dark);
    }

    &__input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid var(--color-grey-light);
      border-radius: 0.25rem;
      font-size: 1rem;
      transition: border-color 0.15s ease-in-out;

      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 0.2rem rgba(0, 35, 35, 0.25);
      }

      &:disabled {
        background-color: var(--color-grey-lighten);
        cursor: not-allowed;
      }
    }
  }

  .row {
    margin-bottom: 1rem;
  }
}
</style>
